import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Search } from 'lucide-react'
import { useShipment } from '@/hooks/useShipments'
import { AppIcon } from '@/components/ui/app-icon'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { StatusBadge } from '@/components/ui/status-badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { SHIPMENT_STATUS } from '@/lib/constants'
import { formatDateTime } from '@/lib/utils'
import { useToast } from '@/hooks/useToast'
import { shipmentsApi } from '@/lib/api'
import type { ShipmentItemType } from '@/types'

const TYPE_LABELS: Record<ShipmentItemType, string> = {
  km: 'КМ',
  box: 'Коробка',
  pallet: 'Паллета',
}

export function WorkerShipmentDetail() {
  const { shipmentId } = useParams<{ shipmentId: string }>()
  const { data: shipment, isLoading, refetch } = useShipment(shipmentId!)
  const { toast } = useToast()

  const [codeInput, setCodeInput] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResult, setSearchResult] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null)

  if (isLoading || !shipment) return <LoadingSpinner className="mt-20" />

  const meta = SHIPMENT_STATUS[shipment.status]
  const additions = shipment.additions ?? []
  const additionsCount = additions.length
  const palletsCount = additions.reduce((sum, a) => sum + (a.pallets_count || 0), 0)
  const boxesCount = additions.reduce((sum, a) => sum + (a.boxes_count || 0), 0)
  const kmCount = additions.reduce((sum, a) => sum + (a.km_count || 0), 0)

  const handleAddCode = async () => {
    const code = codeInput.trim()
    if (!code) {
      toast.error('Введите код')
      return
    }

    setIsSearching(true)
    setSearchResult(null)

    try {
      const { data } = await shipmentsApi.searchCode(code)

      if (!data.found) {
        setSearchResult({ type: 'error', message: data.message || 'Код не найден' })
        return
      }

      const exists = additions.some(a => a.code === code || a.code === data.code)
      if (exists) {
        setSearchResult({ type: 'warning', message: 'Дубликат! Этот код уже добавлен в отгрузку' })
        return
      }

      await shipmentsApi.addItem(shipmentId!, code)

      const typeLabel = data.type ? TYPE_LABELS[data.type] : 'код'
      setSearchResult({
        type: 'success',
        message: `${typeLabel} добавлен. Задание: ${data.task_display_id || data.task_id}`,
      })
      setCodeInput('')
      refetch()
    } catch (err: unknown) {
      const resp = (err as { response?: { data?: { error?: string } } })?.response?.data
      setSearchResult({ type: 'error', message: resp?.error || 'Ошибка добавления' })
    } finally {
      setIsSearching(false)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <Link to="/worker/shipments">
          <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4" /></Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Отгрузка {shipment.id}</h1>
        </div>
        <StatusBadge color={meta.color} textColor={meta.textColor}>{meta.label}</StatusBadge>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow p-5 mb-6">
        <div className="grid grid-cols-3 gap-4 text-sm mb-4">
          <div>
            <span className="text-gray-500">Дата создания:</span>
            <p className="font-medium">{formatDateTime(shipment.created_at)}</p>
          </div>
          <div>
            <span className="text-gray-500">Статус:</span>
            <p><StatusBadge color={meta.color} textColor={meta.textColor}>{meta.label}</StatusBadge></p>
          </div>
          <div>
            <span className="text-gray-500">Экспорт:</span>
            <p>{shipment.exported ? <span className="text-green-600 font-medium inline-flex items-center gap-1"><AppIcon name="reports" className="h-3 w-3" /> Да</span> : <span className="text-gray-400">Нет</span>}</p>
          </div>
        </div>
        {shipment.comment?.trim() ? (
          <div className="mb-4 text-sm">
            <span className="text-gray-500">Комментарий:</span>
            <p className="font-medium whitespace-pre-wrap mt-1">{shipment.comment}</p>
          </div>
        ) : null}
        <div className="grid grid-cols-4 gap-4 text-center border-t pt-4">
          <div>
            <p className="text-gray-500 text-xs">Позиций</p>
            <p className="text-2xl font-bold text-gray-800">{additionsCount}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs inline-flex items-center gap-1 justify-center"><AppIcon name="pallet" className="h-3.5 w-3.5" /> Паллет</p>
            <p className="text-2xl font-bold text-blue-600">{palletsCount}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs inline-flex items-center gap-1 justify-center"><AppIcon name="box" className="h-3.5 w-3.5" /> Коробок</p>
            <p className="text-2xl font-bold text-cyan-600">{boxesCount}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs inline-flex items-center gap-1 justify-center"><AppIcon name="labels" className="h-3.5 w-3.5" /> КМ</p>
            <p className="text-2xl font-bold text-green-600">{kmCount}</p>
          </div>
        </div>
      </div>

      {shipment.status === 'draft' && (
        <div className="bg-white rounded-lg shadow p-5 mb-6">
          <h2 className="font-semibold text-gray-900 mb-3">Добавить код</h2>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">КМ / КА коробки / КА паллеты:</label>
              <Input
                placeholder="КМ или (00)146207654100000185"
                value={codeInput}
                onChange={e => setCodeInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddCode()}
              />
            </div>
            <Button onClick={handleAddCode} isLoading={isSearching}>
              <Search className="w-4 h-4 mr-1" /> Найти и добавить
            </Button>
          </div>
          {searchResult && (
            <div className={`mt-3 rounded-lg p-3 text-sm ${
              searchResult.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
              searchResult.type === 'warning' ? 'bg-yellow-50 border border-yellow-200 text-yellow-700' :
              'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {searchResult.message}
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b">
          <h2 className="font-semibold text-gray-900">Позиции ({additionsCount})</h2>
        </div>
        {additions.length === 0 ? (
          <div className="text-center text-gray-500 py-10">Нет позиций.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-2">№</th>
                <th className="px-4 py-2">Тип</th>
                <th className="px-4 py-2">Код</th>
                <th className="px-4 py-2">GTIN</th>
                <th className="px-4 py-2">Задание</th>
                <th className="px-4 py-2">Состав</th>
                <th className="px-4 py-2">Добавлена</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {additions.map((a, i) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{i + 1}</td>
                  <td className="px-4 py-2">{TYPE_LABELS[a.added_as_type]}</td>
                  <td className="px-4 py-2 font-mono text-xs max-w-xs truncate" title={a.code}>{a.code}</td>
                  <td className="px-4 py-2 font-mono text-xs">{a.gtin}</td>
                  <td className="px-4 py-2 font-mono text-xs">{a.task_display_id || a.task_id}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">{a.items_count} поз.</td>
                  <td className="px-4 py-2 text-gray-500">{formatDateTime(a.added_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
